/* ---------------------------------------------------------------------------
   Client-side clip validation, run BEFORE a byte is uploaded.

   A clip that fails halfway through a round ruins the round for everybody, so
   every rejection here is loud, specific, and happens while the player is still
   standing in the lobby with the file picker open.

   Rules and limits come from convex/lib/game.ts — never restate them here.
   --------------------------------------------------------------------------- */

import {
  ACCEPTED_EXTENSIONS,
  ACCEPTED_MIME,
  MAX_CLIP_BYTES,
  MAX_CLIP_SEC,
  MIN_CLIP_SEC,
} from "../../convex/lib/game";
import { getAudioContext, isAudioSupported, resumeIfSuspended } from "./audio";
import { waitForMetadata } from "./videoTiming";

export type ClipRejectionReason =
  | "empty"
  | "too-large"
  | "unsupported-extension"
  | "unsupported-type"
  | "undecodable"
  | "unknown-duration"
  | "too-short"
  | "too-long"
  | "no-audio"
  | "aborted";

/** How we worked out that the clip has (or hasn't) an audio track. */
export type AudioEvidence =
  | "moz-has-audio"
  | "audio-tracks"
  | "decoded-byte-count"
  | "analyser"
  | "decoded-buffer"
  | "assumed";

export interface ClipValidationSuccess {
  ok: true;
  file: File;
  durationSec: number;
  width: number;
  height: number;
  sizeBytes: number;
  mimeType: string;
  extension: string;
  hasAudio: true;
  /** "assumed" means no probe could prove it either way — we let it through. */
  audioEvidence: AudioEvidence;
}

export interface ClipValidationFailure {
  ok: false;
  reason: ClipRejectionReason;
  /** Human-readable and specific. Safe to render verbatim. */
  message: string;
  sizeBytes: number;
  durationSec?: number;
}

export type ClipValidationResult = ClipValidationSuccess | ClipValidationFailure;

export interface ValidateClipOptions {
  signal?: AbortSignal;
  /** Skip audio-track detection entirely (used by tests). Default false. */
  skipAudioCheck?: boolean;
  /** How long the playback probe may run. Default 900ms. */
  audioProbeMs?: number;
}

/** Containers people genuinely try to upload that no browser will play. */
const HOPELESS_EXTENSIONS: Record<string, string> = {
  ".mkv": "MKV files can't play in browsers — re-export as MP4.",
  ".avi": "AVI files can't play in browsers — re-export as MP4.",
  ".wmv": "WMV files can't play in browsers — re-export as MP4.",
  ".flv": "FLV files can't play in browsers — re-export as MP4.",
  ".mpg": "MPEG-1/2 files can't play in browsers — re-export as MP4.",
  ".mpeg": "MPEG-1/2 files can't play in browsers — re-export as MP4.",
  ".ts": "Transport-stream files can't play in browsers — re-export as MP4.",
  ".m2ts": "Blu-ray stream files can't play in browsers — re-export as MP4.",
  ".3gp": "3GP files don't play reliably in browsers — re-export as MP4.",
  ".ogv": "OGV files don't play in Safari — re-export as MP4 or WebM.",
  ".gif": "GIFs have no sound, and you need audio to dub over. Use a video with audio.",
  ".mp3": "That's an audio file. Upload a video clip.",
  ".wav": "That's an audio file. Upload a video clip.",
  ".m4a": "That's an audio file. Upload a video clip.",
  ".jpg": "That's an image. Upload a video clip.",
  ".png": "That's an image. Upload a video clip.",
  ".heic": "That's an image. Upload a video clip.",
};

const AUDIO_DECODE_FULL_LIMIT = 24 * 1024 * 1024;
const AUDIO_DECODE_SLICE_BYTES = 4 * 1024 * 1024;
const AUDIO_PEAK_THRESHOLD = 1e-4;
const DEFAULT_PROBE_MS = 900;

// --- formatting -------------------------------------------------------------

export function formatMegabytes(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  return mb >= 10 ? `${Math.round(mb)} MB` : `${mb.toFixed(1)} MB`;
}

/** "8s" under a minute, "4:07" above it. */
export function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  return `${minutes}:${String(total % 60).padStart(2, "0")}`;
}

export function extensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf(".");
  return dot === -1 ? "" : fileName.slice(dot).toLowerCase();
}

function fail(
  reason: ClipRejectionReason,
  message: string,
  sizeBytes: number,
  durationSec?: number,
): ClipValidationFailure {
  const failure: ClipValidationFailure = { ok: false, reason, message, sizeBytes };
  if (durationSec !== undefined) failure.durationSec = durationSec;
  return failure;
}

// --- container checks -------------------------------------------------------

function checkContainer(file: File): ClipValidationFailure | null {
  const size = file.size;
  const extension = extensionOf(file.name);
  const mimeType = (file.type || "").toLowerCase();

  const hopeless = HOPELESS_EXTENSIONS[extension];
  if (hopeless) return fail("unsupported-extension", hopeless, size);

  const extensionOk = (ACCEPTED_EXTENSIONS as readonly string[]).includes(extension);
  const mimeOk = (ACCEPTED_MIME as readonly string[]).includes(mimeType);

  if (!extensionOk && !mimeOk) {
    if (mimeType && !mimeType.startsWith("video/")) {
      return fail(
        "unsupported-type",
        `That's a ${mimeType} file, not a video. Upload an MP4, MOV or WebM.`,
        size,
      );
    }
    return fail(
      "unsupported-extension",
      extension
        ? `${extension.slice(1).toUpperCase()} files aren't supported. Upload an MP4, MOV or WebM.`
        : "That file has no extension, so we can't tell what it is. Upload an MP4, MOV or WebM.",
      size,
    );
  }

  return null;
}

// --- duration ---------------------------------------------------------------

/**
 * WebM files written by streaming encoders report Infinity until you seek past
 * the end. This is the standard trick to force a real duration out of them.
 */
async function resolveDuration(video: HTMLVideoElement): Promise<number> {
  if (Number.isFinite(video.duration) && video.duration > 0) return video.duration;

  return new Promise<number>((resolve) => {
    const cleanup = () => {
      video.removeEventListener("durationchange", onChange);
      window.clearTimeout(timer);
    };
    const onChange = () => {
      if (Number.isFinite(video.duration) && video.duration > 0) {
        cleanup();
        video.currentTime = 0;
        resolve(video.duration);
      }
    };
    const timer = window.setTimeout(() => {
      cleanup();
      resolve(Number.NaN);
    }, 3_000);

    video.addEventListener("durationchange", onChange);
    try {
      video.currentTime = 1e101;
    } catch {
      cleanup();
      resolve(Number.NaN);
    }
  });
}

// --- audio detection --------------------------------------------------------

interface AudioProbeResult {
  /** null means "could not tell" — never reject on null. */
  hasAudio: boolean | null;
  evidence: AudioEvidence;
}

interface AudioProbeFlags {
  mozHasAudio?: boolean;
  webkitAudioDecodedByteCount?: number;
  audioTracks?: { length: number };
}

/**
 * Cheap flag probe.
 *
 * ONLY a positive result is trusted here. A negative is returned as `null`
 * (inconclusive) so the caller keeps probing, because on iOS Safari
 * `video.audioTracks` exists but is populated lazily — at `loadedmetadata` it
 * reads `length === 0` for a file that plainly has sound. Treating that zero as
 * proof rejected valid clips with "this clip has no audio track", which is the
 * worst possible failure: it blocks the player from a file that works.
 *
 * `mozHasAudio` is the one exception. Firefox sets it to a real boolean once
 * metadata is in, and a `false` there is genuinely reliable.
 */
function flagProbe(video: HTMLVideoElement): AudioProbeResult | null {
  const flags = video as unknown as AudioProbeFlags;

  if (typeof flags.mozHasAudio === "boolean") {
    return { hasAudio: flags.mozHasAudio, evidence: "moz-has-audio" };
  }
  if (flags.audioTracks && (flags.audioTracks.length ?? 0) > 0) {
    return { hasAudio: true, evidence: "audio-tracks" };
  }
  if (typeof flags.webkitAudioDecodedByteCount === "number" && flags.webkitAudioDecodedByteCount > 0) {
    return { hasAudio: true, evidence: "decoded-byte-count" };
  }
  return null;
}

/**
 * Play a fraction of a second through an AnalyserNode and look for signal.
 * The element is routed into the audio graph rather than the speakers, so this
 * is inaudible even though the element is unmuted.
 *
 * A silent result is inconclusive (the clip may simply open on silence), so
 * this only ever returns `true` or `null`.
 */
async function analyserProbe(
  video: HTMLVideoElement,
  probeMs: number,
  signal?: AbortSignal,
): Promise<AudioProbeResult | null> {
  if (!isAudioSupported()) return null;
  const state = await resumeIfSuspended();
  if (state !== "running" && state !== "uninitialized") return null;

  let source: MediaElementAudioSourceNode | null = null;
  let analyser: AnalyserNode | null = null;

  try {
    const ctx = getAudioContext();
    if (ctx.state !== "running") return null;

    source = ctx.createMediaElementSource(video);
    analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    video.muted = false;
    video.volume = 1;

    try {
      const play = video.play();
      if (play && typeof play.then === "function") await play;
    } catch {
      return null;
    }

    const samples = new Float32Array(analyser.fftSize);
    const deadline = performance.now() + probeMs;
    let peak = 0;

    while (performance.now() < deadline) {
      if (signal?.aborted) break;
      analyser.getFloatTimeDomainData(samples);
      for (let i = 0; i < samples.length; i++) {
        const magnitude = Math.abs(samples[i]);
        if (magnitude > peak) peak = magnitude;
      }
      if (peak > AUDIO_PEAK_THRESHOLD) break;
      await new Promise((resolve) => window.setTimeout(resolve, 40));
    }

    video.pause();
    if (peak > AUDIO_PEAK_THRESHOLD) return { hasAudio: true, evidence: "analyser" };

    const flags = flagProbe(video);
    if (flags?.hasAudio) return flags;
    return null;
  } catch {
    return null;
  } finally {
    try {
      video.pause();
      analyser?.disconnect();
      source?.disconnect();
    } catch {
      /* Element was already torn down. */
    }
    video.muted = true;
  }
}

/**
 * Decode the file (or its opening slice) with the AudioContext and look for a
 * non-silent buffer. A decode that succeeds and yields pure silence is a real
 * "this clip has no usable audio"; a decode that fails proves nothing, because
 * a sliced MP4 has no moov box.
 */
async function decodeProbe(file: File, signal?: AbortSignal): Promise<AudioProbeResult | null> {
  if (!isAudioSupported()) return null;

  try {
    const slice =
      file.size <= AUDIO_DECODE_FULL_LIMIT ? file : file.slice(0, AUDIO_DECODE_SLICE_BYTES);
    const bytes = await slice.arrayBuffer();
    if (signal?.aborted) return null;

    const ctx = getAudioContext();
    const buffer = await new Promise<AudioBuffer>((resolve, reject) => {
      const maybe = ctx.decodeAudioData(bytes, resolve, reject);
      if (maybe && typeof maybe.then === "function") maybe.then(resolve, reject);
    });

    // Channels at all means an audio track exists, which is the only thing
    // this check claims to test. Do NOT gate on `isAudibleBuffer` here: we
    // decode the opening slice, and plenty of real clips open on a fade-in or
    // a beat of silence. Rejecting those told the player their clip "has no
    // audio track" when it plainly does.
    return { hasAudio: buffer.numberOfChannels > 0, evidence: "decoded-buffer" };
  } catch {
    return null;
  }
}

async function detectAudio(
  video: HTMLVideoElement,
  file: File,
  probeMs: number,
  signal?: AbortSignal,
): Promise<AudioProbeResult> {
  const flags = flagProbe(video);
  if (flags) return flags;

  const analysed = await analyserProbe(video, probeMs, signal);
  if (analysed) return analysed;

  const decoded = await decodeProbe(file, signal);
  if (decoded) return decoded;

  return { hasAudio: null, evidence: "assumed" };
}

// --- entry point ------------------------------------------------------------

/**
 * Validate a file the player just picked. Never throws: every failure comes
 * back as `{ ok: false, reason, message }` with a message worth showing.
 */
export async function validateClip(
  file: File,
  options: ValidateClipOptions = {},
): Promise<ClipValidationResult> {
  const { signal, skipAudioCheck = false, audioProbeMs = DEFAULT_PROBE_MS } = options;
  const sizeBytes = file.size;

  if (sizeBytes === 0) {
    return fail("empty", "That file is empty. Pick another clip.", sizeBytes);
  }
  if (sizeBytes > MAX_CLIP_BYTES) {
    return fail(
      "too-large",
      `This clip is ${formatMegabytes(sizeBytes)}. The limit is ${formatMegabytes(
        MAX_CLIP_BYTES,
      )} — trim it, or export at a lower quality.`,
      sizeBytes,
    );
  }

  const containerFailure = checkContainer(file);
  if (containerFailure) return containerFailure;

  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.preload = "metadata";
  video.muted = true;
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  video.crossOrigin = "anonymous";
  video.src = url;

  try {
    try {
      await waitForMetadata(video);
    } catch {
      return fail(
        "undecodable",
        "Your browser couldn't decode this video. It may use HEVC/H.265 or another codec browsers can't play — re-export it as H.264 MP4.",
        sizeBytes,
      );
    }
    if (signal?.aborted) return fail("aborted", "Upload cancelled.", sizeBytes);

    const durationSec = await resolveDuration(video);
    if (!Number.isFinite(durationSec) || durationSec <= 0) {
      return fail(
        "unknown-duration",
        "We couldn't read how long this clip is. Re-export it as an MP4 and try again.",
        sizeBytes,
      );
    }
    if (durationSec < MIN_CLIP_SEC) {
      return fail(
        "too-short",
        `This clip is only ${formatDuration(durationSec)}. Clips need to be at least ${formatDuration(
          MIN_CLIP_SEC,
        )} long.`,
        sizeBytes,
        durationSec,
      );
    }
    if (durationSec > MAX_CLIP_SEC) {
      return fail(
        "too-long",
        `This clip is ${formatDuration(durationSec)}. The limit is ${formatDuration(
          MAX_CLIP_SEC,
        )} — trim it before uploading.`,
        sizeBytes,
        durationSec,
      );
    }

    if (video.videoWidth === 0 && video.videoHeight === 0) {
      return fail(
        "undecodable",
        "This file has no video track we can play. Upload an MP4, MOV or WebM video.",
        sizeBytes,
        durationSec,
      );
    }

    let evidence: AudioEvidence = "assumed";
    if (!skipAudioCheck) {
      const probe = await detectAudio(video, file, audioProbeMs, signal);
      if (probe.hasAudio === false) {
        return fail(
          "no-audio",
          "This clip has no audio track. Pick something with sound — the original audio is what everyone hears before they dub over it.",
          sizeBytes,
          durationSec,
        );
      }
      evidence = probe.evidence;
    }
    if (signal?.aborted) return fail("aborted", "Upload cancelled.", sizeBytes);

    return {
      ok: true,
      file,
      durationSec,
      width: video.videoWidth,
      height: video.videoHeight,
      sizeBytes,
      mimeType: file.type || "video/mp4",
      extension: extensionOf(file.name),
      hasAudio: true,
      audioEvidence: evidence,
    };
  } finally {
    try {
      video.pause();
    } catch {
      /* Nothing was playing. */
    }
    video.removeAttribute("src");
    try {
      video.load();
    } catch {
      /* Safari occasionally throws here after a failed decode. */
    }
    URL.revokeObjectURL(url);
  }
}
